import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, UploadCloud, Table2, AlertCircle, Users, FileCheck, GitCompare, HelpCircle, Download, BarChart3,
  ShieldCheck, Settings, Sun, Moon, Bell, Search, Menu, X, UserCircle, KeyRound, LogOut, Landmark } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChangePassword } from "@/pages/Login";
import { useApp } from "@/context/AppContext";
import { api, inr, fmtDate, bg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CommandDialog, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty } from "@/components/ui/command";
import { Button } from "@/components/ui/button";

const NAV = [
  ["/", "Dashboard", LayoutDashboard, "dashboard"], ["/upload", "Upload Statements", UploadCloud, "upload"], ["/accounts", "Bank Accounts", Landmark, "bank-accounts"],
  ["/transactions", "All Transactions", Table2, "transactions"], ["/attention", "Needs My Attention", AlertCircle, "attention"],
  ["/parties", "Party & Ledger Master", Users, "party-master"], ["/bills", "Bill Matching", FileCheck, "bill-matching"],
  ["/reconciliation", "Bank Reconciliation", GitCompare, "bank-recon"], ["/suspense", "Suspense Account", HelpCircle, "suspense"],
  ["/export", "TallyPrime Export", Download, "tally-export"], ["/reports", "Reports & Analytics", BarChart3, "reports"],
  ["/audit", "Audit History", ShieldCheck, "audit-log"], ["/settings", "Settings", Settings, "settings"],
];

function Sidebar({ counts, onNav }) {
  return (
    <nav className="flex h-full flex-col gap-1 p-4">
      <div className="mb-6 px-2 pt-2">
        <div className="font-display text-lg font-extrabold tracking-tight">ROXY<span className="text-primary"> LEDGER</span> <span className="text-teal-500">AI</span></div>
        <div className="label-xs mt-1">Bank → Tally, verified</div>
      </div>
      {NAV.map(([to, label, Icon, id]) => (
        <NavLink key={to} to={to} end={to === "/"} onClick={onNav} data-testid={`sidebar-item-${id}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${isActive ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
          <Icon className="h-4 w-4" />
          <span className="flex-1">{label}</span>
          {id === "attention" && counts.pending > 0 && <span className="num rounded-full bg-amber-500/20 px-2 text-[11px] text-amber-600 dark:text-amber-400">{counts.pending}</span>}
          {id === "suspense" && counts.suspense > 0 && <span className="num rounded-full bg-rose-500/20 px-2 text-[11px] text-rose-600 dark:text-rose-400">{counts.suspense}</span>}
        </NavLink>
      ))}
      <div className="mt-auto rounded-lg border border-dashed p-3 text-[11px] leading-relaxed text-muted-foreground">
        Shortcuts: <span className="num">Ctrl+K</span> search · <span className="num">/</span> filter · <span className="num">↑↓</span> rows · <span className="num">Enter</span> open
      </div>
    </nav>
  );
}

function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setOpen(false); nav(path); };
  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput placeholder="Search narration, amount, UTR, party, bill no…" value={q} onValueChange={setQ} data-testid="global-search-input" />
      <CommandList>
        <CommandEmpty>{q.length < 2 ? "Type at least 2 characters" : "No results"}</CommandEmpty>
        {res?.transactions?.length > 0 && (
          <CommandGroup heading="Transactions">
            {res.transactions.map((t) => (
              <CommandItem key={t.id} value={`t-${t.id}`} onSelect={() => go(`/transactions?open=${t.id}`)} data-testid="search-result-txn">
                <span className="num mr-2 text-xs text-muted-foreground">{fmtDate(t.txn_date)}</span>
                <span className="flex-1 truncate font-mono text-xs">{t.narration}</span>
                <span className={`num text-xs ${t.direction === "credit" ? "text-emerald-500" : "text-rose-500"}`}>{inr(t.amount)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res?.parties?.length > 0 && (
          <CommandGroup heading="Parties">
            {res.parties.map((p) => <CommandItem key={p.id} value={`p-${p.id}`} onSelect={() => go(`/parties?open=${p.id}`)}>{p.name} <span className="ml-2 text-xs text-muted-foreground">{p.ledger_name}</span></CommandItem>)}
          </CommandGroup>
        )}
        {res?.invoices?.length > 0 && (
          <CommandGroup heading="Invoices">
            {res.invoices.map((i) => <CommandItem key={i.id} value={`i-${i.id}`} onSelect={() => go(`/bills`)}>{i.invoice_no} · {i.party_name} <span className="num ml-auto text-xs">{inr(i.amount)}</span></CommandItem>)}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/notifications", bg).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read").then(load); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span className="num absolute -right-0.5 -top-0.5 rounded-full bg-rose-500 px-1.5 text-[10px] text-white" data-testid="notifications-unread-count">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="border-b px-4 py-3 font-display text-sm font-semibold">Notification center</div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications yet.</div>}
          {items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className="block w-full border-b px-4 py-3 text-left transition-colors hover:bg-accent" data-testid="notification-item">
              <div className={`text-xs font-semibold ${n.kind === "error" ? "text-rose-500" : n.kind === "success" ? "text-emerald-500" : n.kind === "warning" || n.kind === "daily" ? "text-amber-500" : ""}`}>{n.title}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{n.body}</div>
              <div className="num mt-1 text-[10px] text-muted-foreground">{n.at.slice(0, 16).replace("T", " ")}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { businesses, biz, user, chooseBiz, theme, setTheme, logout } = useApp();
  const [pwOpen, setPwOpen] = useState(false);
  const [counts, setCounts] = useState({ pending: 0, suspense: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const load = () => api.get("/dashboard", bg).then((r) => setCounts({ pending: r.data.pending, suspense: r.data.suspense_count })).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    window.addEventListener("roxy:changed", load);
    return () => { clearInterval(t); window.removeEventListener("roxy:changed", load); };
  }, [biz]);
  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r bg-card/60 lg:block">
        <Sidebar counts={counts} />
      </aside>
      {mobile && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="w-72 border-r bg-card"><Sidebar counts={counts} onNav={() => setMobile(false)} /></div>
          <button className="flex-1 bg-black/50" onClick={() => setMobile(false)} aria-label="close menu" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="glass sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b px-4 py-3 md:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-button">{mobile ? <X /> : <Menu className="h-4 w-4" />}</Button>
          <Select value={biz || ""} onValueChange={chooseBiz}>
            <SelectTrigger className="h-9 w-[190px] md:w-[240px]" data-testid="business-selector"><SelectValue placeholder="Business" /></SelectTrigger>
            <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => setSearchOpen(true)} data-testid="global-search-button"
            className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border bg-background/60 px-3 text-sm text-muted-foreground transition-colors hover:border-primary/50 md:max-w-md">
            <Search className="h-4 w-4" /><span className="truncate">Search narration, amount, UTR, party…</span><span className="num ml-auto hidden text-[11px] md:inline">Ctrl K</span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-9 gap-2" data-testid="user-menu-button">
                  <UserCircle className="h-4 w-4" /><span className="hidden max-w-[140px] truncate md:inline">{user?.name}</span>
                  <span className="rounded bg-accent px-1.5 text-[10px] uppercase">{user?.role}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu-item"><KeyRound className="mr-2 h-4 w-4" />Change password</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Notifications />
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-button">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-8 md:px-8 lg:px-10">{children}</main>
      </div>
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent><DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader><ChangePassword onDone={() => setPwOpen(false)} /></DialogContent>
      </Dialog>
    </div>
  );
}
